// HospitalImageUpload.tsx
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { Field, FormRenderProps } from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import ShadowCard from "../../components/common/ShadowCard";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getHospitalImagesByID,
  deleteHospitalImage,
  uploadHospitalMultipleImages,
} from "./services/hospital.service";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import FormAttachmentUpload from "../../components/formFields/FormAttachmentUpload";

const HospitalImageUpload = ({
  formRenderProps,
}: {
  formRenderProps: FormRenderProps;
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const HospitalID = location.state?.HospitalID;
  // const HospitalDetail = useAppSelector(
  //   (state) => state.hospital.HospitalDetail,
  // );
  // const singleImageformLoading = useAppSelector(
  //   (state) => state.hospital.singleImageformLoading,
  // );
  const MultiImageformLoading = useAppSelector(
    (state) => state.hospital.MultiImageformLoading,
  );
  const galleryImages = useAppSelector(
    (state) => state.hospital.HospitalImages,
  );
  const [deleteImageId, setDeleteImageId] = useState<number | null>(null);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  // useEffect(() => {
  //   setFormKey(formKey + 1);
  // }, [HospitalDetail]);

  // useEffect(() => {
  //   if (HospitalID) {
  //     dispatch(getHospitalByID(HospitalID));
  //     dispatch(getHospitalImagesByID(HospitalID));
  //   }
  //   return () => {
  //     dispatch(clearHospitalDetails());
  //   };
  // }, [HospitalID]);

  const handleGallerySubmit = async () => {
    const attachment = formRenderProps.valueGetter("attachment");
    if (!HospitalID) {
      return;
    }

    try {
      if (attachment && attachment?.length > 0) {
        const Images = attachment?.map((fileItem: any) =>
          fileItem.getRawFile(),
        );
        const multiImageData = new FormData();
        Images?.map((image: any) => multiImageData.append("OtherImage", image));
        multiImageData.append("HospitalID", HospitalID);
        const response = await dispatch(
          uploadHospitalMultipleImages(multiImageData),
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(getHospitalImagesByID(HospitalID));
          formRenderProps.onChange("attachment", {
            value: [],
          });
        }
      }
    } catch (error) {
      console.error("Error uploading gallery images:", error);
    }
  };

  const handleDeleteImage = async () => {
    if (deleteImageId) {
      const response = await dispatch(deleteHospitalImage(deleteImageId));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getHospitalImagesByID(HospitalID));
        dispatch(closeDialog());
        setDeleteImageId(null);
      }
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    dispatch(openDialog("deleteHospitalImage"));
    setDeleteImageId(id);
  };

  return (
    <>
      {dialogName === "deleteHospitalImage" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Gallery Image</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this gallery image?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                  gap: "10px",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={handleDeleteImage}
                  type="button"
                  disabled={MultiImageformLoading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}

      <GridLayout style={{ marginTop: 20 }} gap={{ rows: 10, cols: 10 }}>
        {/* Gallery Images Section */}
        <GridLayoutItem colSpan={4}>
          <ShadowCard style={{ padding: 20 }}>
            <GridLayout gap={{ rows: 10, cols: 10 }}>
              <GridLayoutItem colSpan={4}>
                <Typography.h4 style={{ margin: 0, color: "#495057" }}>
                  Gallery Images
                </Typography.h4>
              </GridLayoutItem>

              <GridLayoutItem colSpan={4}>
                {/* <Form
                  key={`multi-${formKey}`}
                  onSubmit={handleGallerySubmit}
                  initialValues={{ attachment: [] }}
                  render={(formRenderProps: FormRenderProps) => (
                    <FormElement> */}
                <GridLayout gap={{ rows: 10 }}>
                  <GridLayoutItem>
                    <Field
                      name="attachment"
                      label="Upload Gallery Images"
                      type="file"
                      accept="image/*"
                      multiple={true}
                      component={FormAttachmentUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography.p
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "5px",
                      }}
                    >
                      You can select multiple images at once. Supported formats:
                      JPG, PNG, WebP
                    </Typography.p>
                    {HospitalID && (
                      <Button
                        type="button"
                        fillMode={"solid"}
                        themeColor={"primary"}
                        disabled={
                          !formRenderProps.valueGetter("attachment") ||
                          formRenderProps.valueGetter("attachment")?.length <
                            1 ||
                          !formRenderProps.allowSubmit ||
                          MultiImageformLoading
                        }
                        onClick={() => handleGallerySubmit()}
                      >
                        Upload Gallery Images
                      </Button>
                    )}
                  </GridLayoutItem>
                </GridLayout>
                {/* </FormElement>
                  )}
                /> */}
              </GridLayoutItem>

              <GridLayoutItem colSpan={4}>
                <hr />
              </GridLayoutItem>

              {galleryImages && galleryImages.length > 0 && (
                <GridLayoutItem colSpan={4}>
                  <Typography.h5 style={{ margin: "0 0 15px 0" }}>
                    Uploaded Images ({galleryImages?.length || 0})
                  </Typography.h5>

                  {/* {(!galleryImages || galleryImages.length === 0) && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px dashed #dee2e6",
                    }}
                  >
                    <Typography.p
                      style={{ marginTop: "10px", color: "#6c757d" }}
                    >
                      No images uploaded yet. Use the form above to add gallery
                      images.
                    </Typography.p>
                  </div>
                )} */}

                  {galleryImages && galleryImages.length > 0 && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: "20px",
                      }}
                    >
                      {galleryImages.map((image: any) => (
                        <div
                          key={image.ID}
                          style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            overflow: "hidden",
                            backgroundColor: "#fff",
                            transition: "transform 0.2s, box-shadow 0.2s",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px rgba(0,0,0,0.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              height: "180px",
                              overflow: "hidden",
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <img
                              src={image.OtherImage}
                              alt="Hospital gallery"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onClick={() =>
                                window.open(image.OtherImage, "_blank")
                              }
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                              }}
                            >
                              <IconButton
                                onClick={() => handleOpenDeleteDialog(image.ID)}
                                themeColor={"error"}
                                size={"small"}
                                fillMode={"solid"}
                                type="button"
                                style={{
                                  height: "32px",
                                  width: "32px",
                                  backgroundColor: "rgba(220, 53, 69, 0.9)",
                                }}
                                icon={MdDelete}
                                iconClassName="absolute-position"
                                iconStyle={{ fontSize: "18px", color: "white" }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </GridLayoutItem>
              )}
            </GridLayout>
          </ShadowCard>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

export default HospitalImageUpload;
